import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { InputTextModule } from 'primeng/inputtext';
import { HttpModule } from '@angular/http';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';   
/**
 * Custom Components
 */
import { AppComponent } from './app.component';
import { BankAccountCardComponent } from './components/bank-account-card/bank-account-card.component';
import { SectionHeaderComponent } from './components/section-header/section-header.component';
import { MainHeaderComponent } from './components/main-header/main-header.component';
import { ConsentConfirmationComponent } from './components/consent-confirmation/consent-confirmation.component';
import { ConsentCardComponent } from './components/consent-card/consent-card.component';
import { RequestblockComponent } from './components/requestblock/requestblock.component';
import { AccountListComponent } from './components/account-list/account-list.component';
import { NotificationComponent } from './components/notification/notification.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ConsentHistoryComponent } from './components/consent-history/consent-history.component';
import { AuthenticateComponent } from './components/authenticate/authenticate.component';
import { ConsentHistoryFilterComponent } from './components/consent-history-filter/consent-history-filter.component';
import { AccountCardComponent } from './components/account-card/account-card.component';
import { AccountLinkComponent } from './components/account-link/account-link.component';
import { AccountDiscoverComponent } from './components/account-discover/account-discover.component';
import { LinkedAccountDetailsComponent } from './components/linked-account-details/linked-account-details.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { FormComponent } from './components/form/form.component';
import { VuaCreationComponent } from './components/vua-creation/vua-creation.component';
import { ConfirmPasswordComponent } from './components/confirm-password/confirm-password.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { PopupConsentPinComponent } from './components/popups/popup-consent-pin/popup-consent-pin.component';
import { MessagesComponent } from './components/popups/messages/messages.component';

/**
 * Custom Services
 */
import { AuthGuardService } from './services/auth-guard.service';
import { FormActionsService } from './services/forms/form-actions.service';
import { FormDefinitionsService } from './services/forms/form-definitions.service';
import { CustomValidatorService } from './services/common/custom-validator.service';

/**
 * Custom Pipes
 */
import { DateFormatPipe } from './pipes/date-format.pipe';
import { PopupComponent } from './components/popup/popup.component';

/**
 * Custom Directives
 */
import { OutsideClickDirective } from './directives/outside-click.directive';
import { ToastComponent } from './components/toast/toast.component';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ConsentListComponent } from './components/consent-list/consent-list.component';
import { AccountLinkedConsentComponent } from './components/account-linked-consent/account-linked-consent.component';
import { AccountDetailComponent } from './components/account-detail/account-detail.component';
import { ConsentTimelineComponent } from './components/consent-timeline/consent-timeline.component';
import { TimelineMessageComponent } from './components/timeline-message/timeline-message.component';
import { AccountIdentifierCardComponent } from './components/account-identifier-card/account-identifier-card.component';
import { IdentifierListComponent } from './components/identifier-list/identifier-list.component';
import { PopupNewIdentifierComponent } from './components/popups/popup-new-identifier/popup-new-identifier.component';
import { SetNicknameComponent } from './components/set-nickname/set-nickname.component';
import { ConsentHistoryDataComponent } from './components/consent-history-data/consent-history-data.component';
import { AppConstants } from './models/appConstants';

/* 
 * Defining Routes
 */
const appRoutes: Routes = [
  { path: 'test',            component: AccountDetailComponent },
  { path: 'login',            component: AuthenticateComponent },
  { path: 'signup',           component: AuthenticateComponent },
  { path: 'vua-creation',     component: VuaCreationComponent },
  { path: 'dashboard',        canActivate: [AuthGuardService], component: DashboardComponent },
  { path: 'profile',          canActivate: [AuthGuardService], component: ProfileComponent },
  { path: 'consent-history',  canActivate: [AuthGuardService], component: ConsentHistoryComponent },
  { path: 'account-list',     canActivate: [AuthGuardService], component: AccountCardComponent },
  { path: 'link-account',     canActivate: [AuthGuardService], component: AccountLinkComponent },
  { path: 'account-discover', canActivate: [AuthGuardService], component:AccountDiscoverComponent },
  { path: 'linked-account',   canActivate: [AuthGuardService], component:LinkedAccountDetailsComponent },
  { path: 'forgot-password',  component: ForgotPasswordComponent },
  { path: 'confirm-password', component: ConfirmPasswordComponent },
  { path: 'consent-confirm/:consentType/:id',  canActivate: [AuthGuardService], component: ConsentConfirmationComponent },
  { path: 'consent-timeline/:id', component: ConsentTimelineComponent},
  { path: 'account-delink',     canActivate: [AuthGuardService], component: AccountLinkComponent },
  { path: 'identifier-list', component: IdentifierListComponent},
  { path: 'set-nickname', component: SetNicknameComponent},
  { path: '**', redirectTo: '/dashboard', pathMatch: 'full' },
];
// canActivate: [AuthGuardService],
@NgModule({
  declarations: [
    AppComponent,
    BankAccountCardComponent,
    SectionHeaderComponent,
    MainHeaderComponent,
    ConsentConfirmationComponent,
    ConsentCardComponent,
    RequestblockComponent,
    AccountListComponent,
    NotificationComponent,
    DashboardComponent,
    ConsentHistoryComponent,
    AuthenticateComponent,
    DateFormatPipe,
    PopupComponent,
    ConsentHistoryFilterComponent,
    AccountCardComponent,
    AccountLinkComponent,
    AccountDiscoverComponent,
    LinkedAccountDetailsComponent,
    OutsideClickDirective,
    ToastComponent,
    ProfileComponent,
    ForgotPasswordComponent,
    ConsentListComponent,
    AccountLinkedConsentComponent,
    AccountDetailComponent,
    FormComponent,
    VuaCreationComponent,
    ConfirmPasswordComponent,
    BreadcrumbComponent,
    ConsentTimelineComponent,
    TimelineMessageComponent,
    PopupConsentPinComponent,
    AccountIdentifierCardComponent,
    IdentifierListComponent,
    PopupNewIdentifierComponent,
    SetNicknameComponent,
    ConsentHistoryDataComponent,
    MessagesComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes),
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    HttpModule
  ],
  providers: [
    FormActionsService,
    FormDefinitionsService,
    CustomValidatorService,
    AppConstants
  ],
  bootstrap: [AppComponent],
  entryComponents: [PopupConsentPinComponent, PopupNewIdentifierComponent, MessagesComponent]
})
export class AppModule { }
